import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Share2, GitBranch, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import caldeiraLogo from '@/assets/caldeira-logo.png';

interface SharedPayload {
  share: { id: string; share_token: string; snapshot_at: string; created_at: string };
  conversation: { id: string; title: string; model: string; created_at: string };
  owner: { id: string; name: string | null; is_me: boolean };
  messages: Array<{ id: string; role: string; content: string; created_at: string }>;
}

export default function SharedConversationPage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<SharedPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forking, setForking] = useState(false);

  useEffect(() => {
    if (!shareToken) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: payload, error: rpcError } = await supabase.rpc('get_shared_conversation', { p_token: shareToken });
      if (cancelled) return;
      if (rpcError) {
        setError(rpcError.message);
      } else if (!payload) {
        setError('Este link de compartilhamento não está mais disponível.');
      } else {
        setData(payload as unknown as SharedPayload);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [shareToken]);

  const handleFork = async () => {
    if (!shareToken) return;
    setForking(true);
    const { data: newId, error: forkError } = await supabase.rpc('fork_shared_conversation', { p_token: shareToken });
    setForking(false);
    if (forkError || !newId) {
      toast.error('Erro ao derivar conversa', { description: forkError?.message });
      return;
    }
    navigate(`/app/chat/${newId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <Loader2 className="w-6 h-6 animate-spin text-tertiary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-surface px-6 text-center">
        <AlertCircle className="w-10 h-10 text-tertiary mb-4" />
        <h1 className="text-lg font-semibold text-primary mb-2">Link indisponível</h1>
        <p className="text-sm text-secondary max-w-md mb-6">{error ?? 'Conversa não encontrada.'}</p>
        <Link to="/app" className="text-sm text-brand-primary hover:underline">Ir para o app</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      {/* Header */}
      <header className="border-b border-color bg-surface px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <Link to="/app" className="flex items-center gap-2">
          <img src={caldeiraLogo} alt="Caldeira AI" className="h-6 w-auto" />
        </Link>
        <div className="flex items-center gap-2 text-xs text-tertiary">
          <Share2 className="w-3.5 h-3.5" />
          Conversa compartilhada
        </div>
      </header>

      {/* Banner */}
      <div className="border-b border-color bg-secondary px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-secondary">
            Compartilhada por <span className="font-medium text-primary">{data.owner.name ?? 'Usuário'}</span>
            {' · '}
            <span className="text-tertiary">snapshot de {new Date(data.share.snapshot_at).toLocaleString('pt-BR')}</span>
          </p>
          {data.owner.is_me && (
            <Link
              to={`/app/chat/${data.conversation.id}`}
              className="text-xs text-brand-primary hover:underline flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Abrir conversa original
            </Link>
          )}
        </div>
      </div>

      {/* Title */}
      <div className="px-6 pt-6 pb-2">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl font-semibold text-primary">{data.conversation.title}</h1>
        </div>
      </div>

      {/* Messages */}
      <main className="flex-1 px-6 pb-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {data.messages.length === 0 ? (
            <p className="text-sm text-tertiary text-center py-12">Esta conversa ainda não tem mensagens.</p>
          ) : (
            data.messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={msg.role === 'user' ? 'max-w-[70%] bg-muted px-4 py-3 rounded-2xl' : 'w-full'}>
                  <div className={`prose prose-sm max-w-none ${msg.role === 'assistant' ? 'font-serif' : 'font-sans text-primary'}`}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Footer CTA */}
      <footer className="border-t border-color bg-surface px-6 py-4 sticky bottom-0">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <p className="text-xs text-tertiary">
            {data.owner.is_me
              ? 'Esta é uma conversa sua. Você pode continuá-la na conversa original.'
              : 'Para continuar essa conversa, crie uma derivação na sua conta.'}
          </p>
          {data.owner.is_me ? (
            <Link
              to={`/app/chat/${data.conversation.id}`}
              className="px-4 py-2 brand-primary rounded-md text-sm font-medium flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir original
            </Link>
          ) : (
            <button
              onClick={handleFork}
              disabled={forking}
              className="px-4 py-2 brand-primary rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {forking ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitBranch className="w-4 h-4" />}
              Continuar em uma nova conversa
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
